import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import type { Extension, Editor } from "@tiptap/core";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}

const MenuButton: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded hover:bg-tiber-200 transition-colors ${
      active ? "bg-tiber-200 text-tiber-900" : "text-tiber-600"
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    {children}
  </button>
);

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  readOnly = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t("common.writeHere");
  const valueRef = useRef(value);
  valueRef.current = value;

  const extensions = [
    StarterKit,
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { class: "text-tiber-500 underline" },
    }),
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Placeholder.configure({
      placeholder: defaultPlaceholder,
      emptyEditorClass: "is-editor-empty",
    }),
  ];

  const editor = useEditor({
    extensions: extensions as Extension[],
    content: value,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[400px] p-5 focus:outline-none",
      },
    },
    onUpdate: ({ editor }: { editor: Editor }) => {
      const html = editor.getHTML();
      if (html !== valueRef.current) {
        onChange(html);
      }
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  if (!editor) return null;

  return (
    <div className={`rich-text-editor ${className}`}>
      {!readOnly && (
        <div className="tiptap-toolbar flex flex-wrap gap-1 p-2 bg-tiber-50 border border-tiber-600 border-b-0 rounded-t-lg">
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive("heading", { level: 1 })}
            title="Heading 1"
          >
            <span className="font-bold text-sm">H1</span>
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive("heading", { level: 2 })}
            title="Heading 2"
          >
            <span className="font-bold text-sm">H2</span>
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive("heading", { level: 3 })}
            title="Heading 3"
          >
            <span className="font-bold text-sm">H3</span>
          </MenuButton>
          <span className="w-px h-6 bg-tiber-300 self-center mx-1" />
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Bold"
          >
            <span className="font-bold">B</span>
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            title="Italic"
          >
            <span className="italic">I</span>
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
            title="Underline"
          >
            <span className="underline">U</span>
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive("strike")}
            title="Strikethrough"
          >
            <span className="line-through">S</span>
          </MenuButton>
          <span className="w-px h-6 bg-tiber-300 self-center mx-1" />
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Bullet list"
          >
            •
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            title="Numbered list"
          >
            1.
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive("blockquote")}
            title="Quote"
          >
            &ldquo;
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            active={editor.isActive("codeBlock")}
            title="Code block"
          >
            &lt;/&gt;
          </MenuButton>
          <MenuButton
            onClick={() => {
              const url = window.prompt("URL:");
              if (url) editor.chain().focus().setLink({ href: url }).run();
            }}
            active={editor.isActive("link")}
            title="Link"
          >
            🔗
          </MenuButton>
        </div>
      )}
      <EditorContent editor={editor} className="tiptap-editor-wrapper" />
      <style>{`
        .rich-text-editor .tiptap-editor-wrapper {
          border: 1px solid #0f1f3d;
          border-radius: ${readOnly ? "8px" : "0 0 8px 8px"};
          min-height: 400px;
        }

        .rich-text-editor .tiptap-editor-wrapper .ProseMirror {
          min-height: 400px;
          padding: 20px;
          line-height: 1.8;
          font-family: inherit;
          font-size: 16px;
        }

        .rich-text-editor .tiptap-editor-wrapper .ProseMirror.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          font-style: normal;
          pointer-events: none;
          float: left;
          height: 0;
        }

        .rich-text-editor .ProseMirror h1 {
          font-size: 2em;
          font-weight: bold;
          margin-top: 0.67em;
          margin-bottom: 0.67em;
        }

        .rich-text-editor .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin-top: 0.83em;
          margin-bottom: 0.83em;
        }

        .rich-text-editor .ProseMirror h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin-top: 1em;
          margin-bottom: 1em;
        }

        .rich-text-editor .ProseMirror strong {
          font-weight: bold;
        }

        .rich-text-editor .ProseMirror em {
          font-style: italic;
        }

        .rich-text-editor .ProseMirror u {
          text-decoration: underline;
        }

        .rich-text-editor .ProseMirror s {
          text-decoration: line-through;
        }

        .rich-text-editor .ProseMirror blockquote {
          border-left: 4px solid #0f1f3d;
          padding-left: 16px;
          margin: 16px 0;
          color: #4b5563;
        }

        .rich-text-editor .ProseMirror pre {
          background: #f3f4f6;
          padding: 16px;
          border-radius: 4px;
          overflow-x: auto;
        }

        .rich-text-editor .ProseMirror ul,
        .rich-text-editor .ProseMirror ol {
          padding-left: 1.5em;
          margin: 12px 0;
        }

        .rich-text-editor .ProseMirror a {
          color: #1cb0a9;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};
